<template>
  <MainHeader />
  <MainSidebar />

  <div class="main-content bg_gray d-flex flex-column transition overflow-hidden">
    <BreadcrumbMenu pageTitle="membership_card_page.list_title" />

    <div class="card border-0 shadow-none rounded-3 mb-4">
      <div class="card-body p-4 p-xl-40">
        <div class="toolbar-card mb-4">
          <div class="toolbar-left">
            <h4 class="mb-1">{{ $t("membership_card_page.list_title") }}</h4>
            <p class="text-muted mb-0">
              {{ $t("membership_card_page.list_description") }}
            </p>
          </div>
          <div class="toolbar-right">
            <div class="search-shell">
              <input
                v-model="searchQuery"
                type="text"
                class="form-control toolbar-search"
                :placeholder="$t('membership_card_page.search_placeholder')"
              />
              <i class="ri-search-line search-icon"></i>
            </div>
            <button
              v-permission="'membership_card_create'"
              class="btn style-one btn-sm d-flex align-items-center gap-2"
              :disabled="loading"
              @click="openCreateModal"
            >
              <i class="ri-bank-card-line"></i>
              <span>{{ $t("membership_card_page.create_button") }}</span>
            </button>
          </div>
        </div>

        <div class="stats-grid mb-4">
          <div class="stat-card">
            <span class="stat-label">{{ $t("membership_card_page.total_cards") }}</span>
            <strong class="stat-value">{{ stats.total }}</strong>
          </div>
          <div class="stat-card">
            <span class="stat-label">{{ $t("membership_card_page.active_cards") }}</span>
            <strong class="stat-value text-success">{{ stats.active }}</strong>
          </div>
          <div class="stat-card">
            <span class="stat-label">{{ $t("membership_card_page.inactive_cards") }}</span>
            <strong class="stat-value text-danger">{{ stats.inactive }}</strong>
          </div>
          <div class="stat-card">
            <span class="stat-label">{{ $t("membership_card_page.stored_balance") }}</span>
            <strong class="stat-value">{{ formatMoney(stats.balance) }}</strong>
          </div>
        </div>

        <a-table
          :columns="columns"
          :data-source="filteredCards"
          :row-key="'id'"
          :pagination="paginationConfig"
          :loading="loading"
          :locale="{ emptyText: $t('membership_card_page.no_cards_found') }"
          class="membership-card-table"
        >
          <template #bodyCell="{ column, record, text }">
            <template v-if="column.key === 'card_number'">
              <span class="code-pill">{{ text || $t("membership_card_page.not_available") }}</span>
            </template>
            <template v-else-if="column.key === 'customer_name'">
              <div class="table-name-cell">
                <strong>{{ record.customer_name || record.customer?.name || $t("membership_card_page.general_customer") }}</strong>
              </div>
            </template>
            <template v-else-if="column.key === 'balance'">
              <span class="fw-semibold">{{ formatMoney(record.balance) }}</span>
            </template>
            <template v-else-if="column.key === 'status'">
              <span
                class="status-badge"
                :class="isActive(record.status) ? 'status-active' : 'status-inactive'"
              >
                {{ isActive(record.status) ? $t("membership_card_page.status_active") : $t("membership_card_page.status_inactive") }}
              </span>
            </template>
            <template v-else-if="column.key === 'issued_at' || column.key === 'expiry_date' || column.key === 'last_used_at'">
              {{ formatDate(text) }}
            </template>
            <template v-else-if="column.key === 'actions'">
              <div class="d-flex justify-content-center gap-2">
                <button
                  v-permission="'membership_card_update'"
                  class="icon-btn"
                  :title="$t('membership_card_page.edit')"
                  @click="openEditModal(record)"
                >
                  <i class="ri-pencil-line"></i>
                </button>
                <button
                  v-permission="'membership_card_delete'"
                  class="icon-btn danger"
                  :title="$t('membership_card_page.delete')"
                  @click="deleteCard(record)"
                >
                  <i class="ri-delete-bin-6-line"></i>
                </button>
                <span
                  v-if="!canUpdateMembershipCard && !canDeleteMembershipCard"
                  class="text-muted small fw-semibold"
                >
                  {{ $t("membership_card_page.view_only") }}
                </span>
              </div>
            </template>
            <template v-else>
              {{ text || $t("membership_card_page.not_available") }}
            </template>
          </template>
        </a-table>
      </div>
    </div>

    <div class="flex-grow-1"></div>
    <MainFooter />
  </div>

  <div
    v-if="showModal"
    class="modal fade show d-block membership-card-modal"
    tabindex="-1"
    aria-modal="true"
    role="dialog"
    @click.self="closeModal"
  >
    <div class="modal-dialog modal-dialog-centered modal-xl">
      <div class="modal-content border-0 shadow-lg rounded-4">
        <div class="modal-header border-bottom-dashed">
          <div>
            <span class="modal-chip">
              {{ selectedCard ? $t("membership_card_page.edit_chip") : $t("membership_card_page.new_chip") }}
            </span>
            <h5 class="modal-title fw-semibold mt-3 mb-1">
              {{ selectedCard ? $t("membership_card_page.update_title") : $t("membership_card_page.create_title") }}
            </h5>
            <p class="text-muted mb-0 modal-copy">
              {{ $t("membership_card_page.modal_description") }}
            </p>
          </div>
          <button
            type="button"
            class="btn-close shadow-none"
            :aria-label="$t('common.close')"
            @click="closeModal"
          ></button>
        </div>

        <div class="modal-body p-4 p-xl-40">
          <div class="membership-card-tip-card mb-4">
            <div class="tip-icon">
              <i class="ri-bank-card-line"></i>
            </div>
            <div>
              <h6 class="mb-1">
                {{ selectedCard ? $t("membership_card_page.edit_tip_title") : $t("membership_card_page.create_tip_title") }}
              </h6>
              <p class="mb-0 text-muted">
                {{ $t("membership_card_page.tip_description") }}
              </p>
            </div>
          </div>

          <div class="row g-3">
            <div class="col-md-6">
              <div class="field-header">
                <label class="form-label fw-semibold mb-0">{{ $t("membership_card_page.customer") }}</label>
                <span class="field-caption">{{ $t("membership_card_page.customer_caption") }}</span>
              </div>
              <select
                v-model="form.customer_id"
                class="form-select modal-input"
                :class="{ 'is-invalid': errors.customer_id }"
              >
                <option :value="null">{{ $t("membership_card_page.general_customer") }}</option>
                <option
                  v-for="customer in customers"
                  :key="customer.id"
                  :value="Number(customer.id)"
                >
                  {{ customer.name }}
                </option>
              </select>
              <div v-if="errors.customer_id" class="invalid-feedback d-block">
                {{ errors.customer_id[0] }}
              </div>
            </div>

            <div class="col-md-6">
              <div class="field-header">
                <label class="form-label fw-semibold mb-0">{{ $t("membership_card_page.card_name") }}</label>
                <span class="field-caption">{{ $t("membership_card_page.card_name_caption") }}</span>
              </div>
              <input
                v-model="form.card_name"
                type="text"
                class="form-control modal-input"
                :class="{ 'is-invalid': errors.card_name }"
                :placeholder="$t('membership_card_page.card_name_placeholder')"
              />
              <div v-if="errors.card_name" class="invalid-feedback d-block">
                {{ errors.card_name[0] }}
              </div>
            </div>

            <div class="col-md-6">
              <div class="field-header">
                <label class="form-label fw-semibold mb-0">{{ $t("membership_card_page.card_number") }}</label>
                <span class="field-caption">{{ $t("membership_card_page.card_number_caption") }}</span>
              </div>
              <div class="reference-input-wrap">
                <input
                  v-model="form.card_number"
                  type="text"
                  class="form-control modal-input"
                  :class="{ 'is-invalid': errors.card_number }"
                  :placeholder="$t('membership_card_page.card_number_placeholder')"
                  @input="form.card_number = String(form.card_number || '').toUpperCase()"
                />
                <button
                  type="button"
                  class="btn btn-light generate-code-btn"
                  :disabled="loading"
                  @click="generateCardNumber"
                >
                  <i class="ri-refresh-line me-1"></i>
                  {{ $t("membership_card_page.generate_code") }}
                </button>
              </div>
              <div v-if="errors.card_number" class="invalid-feedback d-block">
                {{ errors.card_number[0] }}
              </div>
            </div>

            <div class="col-md-3">
              <div class="field-header">
                <label class="form-label fw-semibold mb-0">{{ $t("membership_card_page.balance") }}</label>
                <span class="field-caption">{{ $t("membership_card_page.balance_caption") }}</span>
              </div>
              <input
                v-model.number="form.balance"
                type="number"
                min="0"
                step="0.01"
                class="form-control modal-input"
                :class="{ 'is-invalid': errors.balance }"
                :placeholder="$t('membership_card_page.balance_placeholder')"
              />
              <div v-if="errors.balance" class="invalid-feedback d-block">
                {{ errors.balance[0] }}
              </div>
            </div>

            <div class="col-md-3">
              <div class="field-header">
                <label class="form-label fw-semibold mb-0">{{ $t("membership_card_page.status") }}</label>
                <span class="field-caption">{{ $t("membership_card_page.status_caption") }}</span>
              </div>
              <select v-model.number="form.status" class="form-select modal-input">
                <option :value="1">{{ $t("membership_card_page.status_active") }}</option>
                <option :value="0">{{ $t("membership_card_page.status_inactive") }}</option>
              </select>
            </div>

            <div class="col-md-6">
              <div class="field-header">
                <label class="form-label fw-semibold mb-0">{{ $t("membership_card_page.issued_date") }}</label>
                <span class="field-caption">{{ $t("membership_card_page.issued_date_caption") }}</span>
              </div>
              <input
                v-model="form.issued_at"
                type="date"
                class="form-control modal-input"
                :class="{ 'is-invalid': errors.issued_at }"
              />
              <div v-if="errors.issued_at" class="invalid-feedback d-block">
                {{ errors.issued_at[0] }}
              </div>
            </div>

            <div class="col-md-6">
              <div class="field-header">
                <label class="form-label fw-semibold mb-0">{{ $t("membership_card_page.expiry_date") }}</label>
                <span class="field-caption">{{ $t("membership_card_page.expiry_date_caption") }}</span>
              </div>
              <input
                v-model="form.expiry_date"
                type="date"
                class="form-control modal-input"
                :class="{ 'is-invalid': errors.expiry_date }"
              />
              <div v-if="errors.expiry_date" class="invalid-feedback d-block">
                {{ errors.expiry_date[0] }}
              </div>
            </div>

            <div class="col-12">
              <div class="field-header">
                <label class="form-label fw-semibold mb-0">{{ $t("membership_card_page.note") }}</label>
                <span class="field-caption">{{ $t("membership_card_page.note_caption") }}</span>
              </div>
              <textarea
                v-model="form.note"
                rows="4"
                class="form-control modal-input modal-textarea"
                :class="{ 'is-invalid': errors.note }"
                :placeholder="$t('membership_card_page.note_placeholder')"
              ></textarea>
              <div v-if="errors.note" class="invalid-feedback d-block">
                {{ errors.note[0] }}
              </div>
            </div>
          </div>

          <div class="d-flex justify-content-between align-items-center mt-4 pt-2">
            <button type="button" class="btn btn-light px-4" @click="closeModal">
              {{ $t("common.cancel") }}
            </button>
            <button
              type="button"
              class="btn style-one px-4 h-55"
              :disabled="loading || !canSaveCurrentCard"
              @click="saveCard"
            >
              <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
              {{ loading ? $t("membership_card_page.saving") : selectedCard ? $t("membership_card_page.update_action") : $t("membership_card_page.create_action") }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, watch } from "vue";
import Swal from "sweetalert2";
import { useI18n } from "vue-i18n";

import BreadcrumbMenu from "../../components/Common/BreadcrumbMenu.vue";
import MainFooter from "../../components/Layouts/MainFooter.vue";
import MainHeader from "../../components/Layouts/MainHeader.vue";
import MainSidebar from "../../components/Layouts/MainSidebar.vue";
import customerService, { type Customer } from "../../services/customer";
import membershipCardService, {
  type MembershipCard,
  type MembershipCardPayload,
} from "../../services/membershipCard";
import { hasPermission } from "../../utils/permission";

type ApiError = {
  response?: {
    status?: number;
    data?: {
      message?: string;
      errors?: Record<string, string[]>;
    };
  };
};

interface MembershipCardFormState {
  customer_id: number | null;
  card_name: string;
  card_number: string;
  balance: number;
  status: number;
  issued_at: string;
  expiry_date: string;
  note: string;
}

const cards = ref<MembershipCard[]>([]);
const customers = ref<Customer[]>([]);
const searchQuery = ref("");
const showModal = ref(false);
const selectedCard = ref<MembershipCard | null>(null);
const loading = ref(false);
const errors = ref<Record<string, string[]>>({});
const { t, locale } = useI18n();

const createDefaultForm = (): MembershipCardFormState => ({
  customer_id: null,
  card_name: "",
  card_number: "",
  balance: 0,
  status: 1,
  issued_at: "",
  expiry_date: "",
  note: "",
});

const form = ref<MembershipCardFormState>(createDefaultForm());
const canCreateMembershipCard = hasPermission("membership_card_create");
const canUpdateMembershipCard = hasPermission("membership_card_update");
const canDeleteMembershipCard = hasPermission("membership_card_delete");
const canSaveCurrentCard = computed(() =>
  selectedCard.value ? canUpdateMembershipCard : canCreateMembershipCard
);

const columns = computed(() => [
  { title: t("membership_card_page.table_card_number"), dataIndex: "card_number", key: "card_number", align: "center" as const, width: 180 },
  { title: t("membership_card_page.table_card_name"), dataIndex: "card_name", key: "card_name", align: "center" as const, width: 220 },
  { title: t("membership_card_page.table_customer"), dataIndex: "customer_name", key: "customer_name", align: "center" as const, width: 220 },
  { title: t("membership_card_page.table_balance"), dataIndex: "balance", key: "balance", align: "center" as const, width: 140 },
  { title: t("membership_card_page.table_issued"), dataIndex: "issued_at", key: "issued_at", align: "center" as const, width: 140 },
  { title: t("membership_card_page.table_expiry"), dataIndex: "expiry_date", key: "expiry_date", align: "center" as const, width: 140 },
  { title: t("membership_card_page.table_last_used"), dataIndex: "last_used_at", key: "last_used_at", align: "center" as const, width: 180 },
  { title: t("membership_card_page.table_status"), dataIndex: "status", key: "status", align: "center" as const, width: 140 },
  { title: t("membership_card_page.table_action"), key: "actions", align: "center" as const, width: 140 },
]);

const pageSizeOptions = ref(["10", "25", "50", "100", "1000"]);

const paginationConfig = computed(() => ({
  showSizeChanger: true,
  pageSizeOptions: pageSizeOptions.value,
  showTotal: (total: number, range: [number, number]) =>
    t("membership_card_page.pagination_total", {
      start: range[0],
      end: range[1],
      total,
    }),
  defaultPageSize: 10,
  locale: { items_per_page: t("membership_card_page.items_per_page") },
}));

const fetchInitialData = async () => {
  loading.value = true;
  try {
    const [cardResult, customerResult] = await Promise.allSettled([
      membershipCardService.getMembershipCards(),
      customerService.getCustomers(),
    ]);

    cards.value = cardResult.status === "fulfilled" ? cardResult.value : [];
    customers.value =
      customerResult.status === "fulfilled" ? customerResult.value : [];

    if (cardResult.status === "rejected") {
      throw cardResult.reason;
    }
  } catch (error) {
    await Swal.fire({
      icon: "error",
      title: t("membership_card_page.load_failed"),
      text: t("membership_card_page.load_failed_text"),
    });
  } finally {
    loading.value = false;
  }
};

const filteredCards = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();
  if (!query) return cards.value;

  return cards.value.filter((card) =>
    [
      card.card_number,
      card.card_name,
      card.customer_name,
      card.customer?.name,
      card.note,
    ]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(query))
  );
});

const stats = computed(() => {
  const total = cards.value.length;
  const active = cards.value.filter((card) => isActive(card.status)).length;
  const balance = cards.value.reduce(
    (sum, card) => sum + Number(card.balance || 0),
    0
  );

  return {
    total,
    active,
    inactive: Math.max(total - active, 0),
    balance,
  };
});

watch(
  filteredCards,
  (newValue) => {
    const totalCount = newValue.length > 0 ? newValue.length : 1000;
    pageSizeOptions.value = ["10", "25", "50", "100", String(totalCount)];
  },
  { immediate: true }
);

const isActive = (status?: number | boolean) =>
  String(status ?? 1) !== "0" && String(status ?? 1) !== "false";

const formatMoney = (value?: number | string) =>
  `$${Number(value || 0).toFixed(2)}`;

const formatDate = (value?: string | null) => {
  if (!value) return t("membership_card_page.not_available");

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return t("membership_card_page.not_available");

  const hasTime = /T|\d{2}:\d{2}/.test(value);
  const localeCode = String(locale.value).startsWith("km") ? "km-KH" : "en-GB";
  return hasTime
    ? date.toLocaleString(localeCode, {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : date.toLocaleDateString(localeCode);
};

const resetForm = () => {
  form.value = createDefaultForm();
  errors.value = {};
};

const openCreateModal = () => {
  if (!canCreateMembershipCard) {
    Swal.fire({
      icon: "warning",
      title: t("membership_card_page.permission_required"),
      text: t("membership_card_page.create_permission_text"),
    });
    return;
  }

  selectedCard.value = null;
  resetForm();
  generateCardNumber();
  showModal.value = true;
};

const openEditModal = (card: MembershipCard) => {
  if (!canUpdateMembershipCard) {
    Swal.fire({
      icon: "warning",
      title: t("membership_card_page.permission_required"),
      text: t("membership_card_page.edit_permission_text"),
    });
    return;
  }

  selectedCard.value = card;
  errors.value = {};
  form.value = {
    customer_id: card.customer_id ? Number(card.customer_id) : null,
    card_name: card.card_name || "",
    card_number: card.card_number || "",
    balance: Number(card.balance || 0),
    status: Number(card.status ?? 1),
    issued_at: card.issued_at || "",
    expiry_date: card.expiry_date || "",
    note: card.note || "",
  };
  showModal.value = true;
};

const closeModal = () => {
  showModal.value = false;
  errors.value = {};
};

const generateCardNumber = () => {
  const nextNumber =
    cards.value
      .map((item) => {
        const match = String(item.card_number || "").match(/(\d+)\s*$/);
        return match ? Number(match[1]) : 0;
      })
      .reduce((max, current) => Math.max(max, current), 0) + 1;

  form.value.card_number = `CARD${String(nextNumber).padStart(3, "0")}`;
};

const saveCard = async () => {
  if (!canSaveCurrentCard.value) {
    await Swal.fire({
      icon: "warning",
      title: t("membership_card_page.permission_required"),
      text: selectedCard.value
        ? t("membership_card_page.update_permission_text")
        : t("membership_card_page.create_permission_text"),
    });
    return;
  }

  loading.value = true;
  errors.value = {};

  try {
    const payload: MembershipCardPayload = {
      customer_id:
        form.value.customer_id !== null &&
        form.value.customer_id !== undefined &&
        Number(form.value.customer_id || 0) > 0
          ? Number(form.value.customer_id)
          : null,
      card_name: form.value.card_name.trim(),
      card_number: form.value.card_number.trim().toUpperCase(),
      balance: Math.max(0, Number(form.value.balance || 0)),
      status: Number(form.value.status ?? 1),
      issued_at: form.value.issued_at || null,
      expiry_date: form.value.expiry_date || null,
      note: form.value.note.trim() || null,
    };

    if (selectedCard.value?.id) {
      await membershipCardService.updateMembershipCard(selectedCard.value.id, payload);
    } else {
      await membershipCardService.createMembershipCard(payload);
    }

    await fetchInitialData();
    await Swal.fire({
      icon: "success",
      title: selectedCard.value ? t("membership_card_page.updated_title") : t("membership_card_page.created_title"),
      timer: 1400,
      showConfirmButton: false,
    });
    closeModal();
  } catch (error: unknown) {
    const apiError = error as ApiError;
    if (apiError.response?.status === 422) {
      errors.value = apiError.response.data?.errors || {};
    } else {
      await Swal.fire({
        icon: "error",
        title: t("membership_card_page.save_failed"),
        text:
          apiError.response?.data?.message ||
          t("membership_card_page.save_failed_text"),
      });
    }
  } finally {
    loading.value = false;
  }
};

const deleteCard = async (card: MembershipCard) => {
  if (!card.id) return;

  if (!canDeleteMembershipCard) {
    await Swal.fire({
      icon: "warning",
      title: t("membership_card_page.permission_required"),
      text: t("membership_card_page.delete_permission_text"),
    });
    return;
  }

  const result = await Swal.fire({
    title: t("membership_card_page.delete_title"),
    text: t("membership_card_page.delete_text", { name: card.card_name }),
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#d33",
    cancelButtonColor: "#6c757d",
    confirmButtonText: t("membership_card_page.delete_confirm"),
    cancelButtonText: t("common.cancel"),
    reverseButtons: true,
  });

  if (!result.isConfirmed) return;

  try {
    Swal.fire({
      title: t("membership_card_page.deleting_title"),
      text: t("membership_card_page.deleting_text"),
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    await membershipCardService.deleteMembershipCard(card.id);
    await fetchInitialData();

    await Swal.fire({
      icon: "success",
      title: t("membership_card_page.deleted_title"),
      text: t("membership_card_page.deleted_text", { name: card.card_name }),
      timer: 1500,
      showConfirmButton: false,
    });
  } catch (error: unknown) {
    const apiError = error as ApiError;
    await Swal.fire({
      icon: "error",
      title: t("membership_card_page.delete_failed"),
      text:
        apiError.response?.data?.message ||
        t("membership_card_page.delete_failed_text"),
    });
  }
};

onMounted(fetchInitialData);
</script>

<style scoped>
.toolbar-card {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 18px;
  padding: 22px;
  border: 1px solid #edf0f7;
  border-radius: 20px;
  background: #fbfcff;
}

.toolbar-right {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}

.search-shell {
  position: relative;
  min-width: 340px;
}

.toolbar-search {
  min-height: 54px;
  padding: 14px 48px 14px 18px;
  border-radius: 16px;
  border: 1px solid #d7def4;
  box-shadow: none;
}

.toolbar-search:focus {
  border-color: #605dff;
  box-shadow: 0 0 0 4px rgba(96, 93, 255, 0.12);
}

.search-icon {
  position: absolute;
  right: 18px;
  top: 50%;
  transform: translateY(-50%);
  color: #7d8aa2;
  pointer-events: none;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
}

.stat-card {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 18px 20px;
  border: 1px solid #edf0f7;
  border-radius: 18px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
}

.stat-label {
  color: #7d8aa2;
  font-size: 13px;
  font-weight: 600;
}

.stat-value {
  color: #0f172a;
  font-size: 24px;
  line-height: 1.1;
}

.membership-card-table :deep(.ant-table-thead > tr > th) {
  font-weight: 700;
}

.code-pill,
.status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 88px;
  padding: 6px 12px;
  border-radius: 999px;
  font-weight: 700;
}

.code-pill {
  background: #eef2ff;
  color: #4f46e5;
}

.table-name-cell {
  display: inline-flex;
  flex-direction: column;
  gap: 4px;
}

.status-badge {
  font-size: 12px;
}

.status-active {
  background: #e8f7ee;
  color: #1f9254;
}

.status-inactive {
  background: #fdecec;
  color: #d33c43;
}

.icon-btn {
  width: 36px;
  height: 36px;
  border: 0;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #eef2ff;
  color: #4f46e5;
  transition: transform 0.2s ease, background-color 0.2s ease;
}

.icon-btn:hover {
  transform: translateY(-1px);
  background: #dfe7ff;
}

.icon-btn.danger {
  background: #fdecec;
  color: #d33c43;
}

.icon-btn.danger:hover {
  background: #fbd8d9;
}

.membership-card-modal {
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(4px);
}

.modal-chip {
  display: inline-flex;
  align-items: center;
  padding: 8px 14px;
  border-radius: 999px;
  background: #edf2ff;
  color: #5d5fef;
  font-weight: 700;
  font-size: 13px;
}

.membership-card-tip-card {
  display: flex;
  gap: 14px;
  align-items: flex-start;
  padding: 16px 18px;
  border: 1px solid #e6ebfb;
  border-radius: 18px;
  background: linear-gradient(180deg, #f8faff 0%, #ffffff 100%);
}

.tip-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 14px;
  background: #eef2ff;
  color: #5d5fef;
  font-size: 20px;
}

.modal-copy {
  font-size: 14px;
}

.field-header {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 10px;
}

.field-caption {
  color: #6b7280;
  font-size: 12px;
}

.reference-input-wrap {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 12px;
  align-items: stretch;
}

.modal-input {
  min-height: 52px;
  border-radius: 16px;
  border: 1px solid #d7def4;
  padding: 14px 16px;
  box-shadow: none;
}

.modal-input:focus {
  border-color: #605dff;
  box-shadow: 0 0 0 4px rgba(96, 93, 255, 0.12);
}

.modal-textarea {
  min-height: 140px;
  resize: vertical;
}

.generate-code-btn {
  min-width: 124px;
  border-radius: 16px;
  border: 1px solid #d7def4;
}

@media (max-width: 991.98px) {
  .stats-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 767.98px) {
  .search-shell {
    min-width: 100%;
  }

  .toolbar-right {
    width: 100%;
  }

  .stats-grid {
    grid-template-columns: repeat(1, minmax(0, 1fr));
  }

  .reference-input-wrap {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
